import {
    Component,
    OnInit,
    OnDestroy,
    ViewChildren,
    ViewChild,
    QueryList,
    Inject,
    Optional,
    ChangeDetectorRef,
    ElementRef,
    AfterViewChecked,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    RdsDialogRef,
    RdsDropdownDirective,
    RDS_DIALOG_DATA,
    RdsDialogService,
} from '@rds/angular-components';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import {
    TypeaheadUser,
    TypeaheadInputComponent,
} from 'src/app/component/typeahead-input/typeahead-input.component';
import { Collaborator, ShareService } from 'src/app/service/share.service';
import {
    AddUserRoleRequest,
    FetchUserDetails,
    UpdateProjectUserRoleRequest,
} from 'src/app/model/user-role.model';
import { DialogService } from 'src/app/service/dialog.service';
import { PermissionService } from 'src/app/modules/ecb-workbench/service/permission.service';
import { DesignerSuiteService } from 'src/app/modules/designer/service/designer-suite.service';
import {
    resourceBundleConstant,
    ResourceBundleConstant,
} from 'src/app/utility/constant/resource-bundle-constant';
import { RolesDescriptionComponent } from './roles-description/roles-description.component';

export interface ShareDialogData {
    user_role: string;
    project_id: string;
    project_version_id: string;
    project_name: string;
    updated_on: string;
    collaborators?: Collaborator[];
    max_owners?: number | null;
    copyLinkUrl?: string;
    /** User IDs currently online, used to show "Currently Online" dots. */
    activeUserIds?: string[];
    /** Role selected for the pending invites, preserved across reopen. */
    pendingInviteRole?: string;
    /** Pending (typed-but-not-yet-invited) users, preserved across dialog
     * reopen (e.g. when viewing the roles description). */
    pendingInvites?: TypeaheadUser[];
}

export interface ShareUser {
    user_id: string;
    name: string;
    email: string;
    role: string;
    avatarUrl: string;
}

@Component({
    selector: 'app-share',
    standalone: false,
    templateUrl: './share.component.html',
    styleUrl: './share.component.scss',
})
export class ShareComponent implements OnInit, OnDestroy, AfterViewChecked {
    @ViewChildren(RdsDropdownDirective)
    dropdowns!: QueryList<RdsDropdownDirective>;
    @ViewChild(TypeaheadInputComponent)
    typeaheadInput!: TypeaheadInputComponent;
    @ViewChild('userListWrapper') userListWrapper!: ElementRef<HTMLElement>;

    private readonly destroy$ = new Subject<void>();
    private readonly searchSubject = new Subject<string>();
    private readonly userSearchSubject = new Subject<string>();
    private readonly scrollHandler = this.onScroll.bind(this);
    public readonly resourceConstant: ResourceBundleConstant =
        resourceBundleConstant;

    // Dialog data
    projectId: string = '';
    projectVersionId: string = '';
    projectName: string = '';
    updatedOn: string = '';

    /** Flag to display sole owner alert when trying to leave project */
    showOwnerMessageAlert = false;
    /** Flag to display editor self-role change alert */
    showEditorSelfRoleAlert = false;
    /** Flag to display max owner limit alert */
    showMaxOwnerAlert = false;

    // Invite section
    inviteRole = 'Editor';
    isInviting = false;
    selectedUsersToInvite: TypeaheadUser[] = [];

    // Typeahead search state
    existingUserSuggestions: TypeaheadUser[] = [];
    userSuggestions: TypeaheadUser[] = [];
    isSearchingUsers = false;
    noUsersFound = false;

    // Filter section
    searchText = '';
    filterRole = 'All Roles';
    maxOwners = 2;

    // User list
    users: ShareUser[] = [];
    filteredUsers: ShareUser[] = [];
    isLoading = false;
    hasScrollbar = false;
    currentUserId = '';

    /** User IDs currently online (for "Currently Online" dots). */
    private readonly activeUserIds = new Set<string>();

    // Available roles
    roles = ['Viewer', 'Commenter', 'Editor', 'Owner'];
    private readonly allRoles = ['Owner', 'Editor', 'Commenter', 'Viewer'];
    filterRoles = ['Owner', 'Editor', 'Commenter', 'Viewer'];

    private readonly defaultAvatarUrl =
        'https://cdn.iconscout.com/icon/free/png-512/free-avatar-icon-svg-download-png-456318.png?f=webp&w=512';

    constructor(
        private readonly dialogRef: RdsDialogRef<ShareComponent>,
        private readonly shareService: ShareService,
        private readonly cdr: ChangeDetectorRef,
        private readonly confirmDialogService: DialogService,
        private readonly rdsDialogService: RdsDialogService,
        public readonly permissionService: PermissionService,
        private readonly designerService: DesignerSuiteService,
        private readonly router: Router,
        @Optional() @Inject(RDS_DIALOG_DATA) public data: ShareDialogData
    ) {
        if (data) {
            this.projectId = data.project_id;
            this.projectVersionId = data.project_version_id;
            this.projectName = data.project_name;
            this.updatedOn = data.updated_on;
            this.maxOwners = this.normalizeOwnerLimit(data.max_owners);
            this.activeUserIds = new Set(data.activeUserIds ?? []);
            // Restore pending invites/role when the dialog is reopened (e.g.
            // after viewing the roles description) so typed users aren't lost.
            if (data.pendingInvites?.length) {
                this.selectedUsersToInvite = [...data.pendingInvites];
            }
            if (data.pendingInviteRole) {
                this.inviteRole = data.pendingInviteRole;
            }
        }
    }

    /** Whether a user is currently online. */
    isUserOnline(userId: string): boolean {
        return this.activeUserIds.has(userId);
    }

    private capitalizeRole(role: string): string {
        if (!role) return 'Viewer';
        return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
    }

    getOwnerCount(): number {
        return this.users.filter((user) => user.role.toLowerCase() === 'owner')
            .length;
    }

    private normalizeOwnerLimit(value?: number | null): number {
        if (
            typeof value !== 'number' ||
            !Number.isSafeInteger(value) ||
            value <= 0
        ) {
            return 2;
        }
        return value;
    }

    get maxOwnersMessage(): string {
        const ownerLabel = this.maxOwners === 1 ? 'owner' : 'owners';
        return (
            `This project can have a maximum of ${this.maxOwners} ${ownerLabel}. ` +
            'Contact admin to add more owners.'
        );
    }

    updateRolesBasedOnOwnerCount(): void {
        if (this.permissionService.canAddOwner()) {
            this.roles = [...this.allRoles];
        } else {
            this.roles = this.allRoles.filter((role) => role !== 'Owner');
        }

        if (this.getOwnerCount() > 1) {
            this.showOwnerMessageAlert = false;
        }
        this.showEditorSelfRoleAlert = false;
        this.showMaxOwnerAlert = false;
    }

    isDropdownOptionDisabled(_role: string): boolean {
        return false;
    }

    private isOwnerLimitReached(): boolean {
        return this.getOwnerCount() >= this.maxOwners;
    }

    isDisabled(role?: string): boolean {
        if (role === 'Owner') {
            return !this.permissionService.canAddOwner();
        }
        if (this.permissionService.canAddUser()) {
            return false;
        }
        return true;
    }

    ngOnInit(): void {
        this.loadUsers();
        this.updateRolesBasedOnOwnerCount();
        this.setupSearchDebounce();
        this.setupUserSearchDebounce();
        document.addEventListener('scroll', this.scrollHandler, true);
    }

    ngOnDestroy(): void {
        document.removeEventListener('scroll', this.scrollHandler, true);
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngAfterViewChecked(): void {
        this.checkScrollbar();
    }

    private checkScrollbar(): void {
        if (this.userListWrapper?.nativeElement) {
            const el = this.userListWrapper.nativeElement;
            const hasScroll = el.scrollHeight > el.clientHeight;
            if (this.hasScrollbar !== hasScroll) {
                this.hasScrollbar = hasScroll;
                this.cdr.detectChanges();
            }
        }
    }

    private onScroll(): void {
        this.dropdowns?.forEach((dropdown) => dropdown.hide());
    }

    private setupSearchDebounce(): void {
        this.searchSubject.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.filterUsers();
            this.cdr.detectChanges();
        });
    }

    private setupUserSearchDebounce(): void {
        this.userSearchSubject
            .pipe(debounceTime(500), takeUntil(this.destroy$))
            .subscribe((query: string) => {
                this.fetchUserSuggestions(query);
            });
    }

    loadUsers(): void {
        if (this.projectId && this.projectVersionId) {
            this.isLoading = true;
            this.shareService
                .fetchCollaborators(this.projectId, this.projectVersionId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (response) => {
                        if (response?.is_success && response.data) {
                            this.maxOwners = this.normalizeOwnerLimit(
                                response.data.max_owners
                            );
                            this.populateUsersFromRoles(
                                response.data.collaborators
                            );
                        }
                        this.isLoading = false;
                        this.filterUsers();
                        this.updateRolesBasedOnOwnerCount();
                        this.cdr.detectChanges();
                    },
                    error: () => {
                        this.isLoading = false;
                        this.filterUsers();
                        this.cdr.detectChanges();
                    },
                });
        } else {
            this.filterUsers();
        }
    }

    private populateUsersFromRoles(userList: any[]): void {
        const currentCollaborator = userList.find((c: any) => c.current_user);
        if (currentCollaborator) {
            this.currentUserId = currentCollaborator.user_id;
        }
        this.users = userList.map((userData: any) => ({
            user_id: userData.user_id,
            name:
                userData.full_name ||
                `${userData.first_name} ${userData.last_name}`.trim(),
            email: userData.email,
            role: this.capitalizeRole(userData.role),
            avatarUrl: this.defaultAvatarUrl,
        }));

        this.existingUserSuggestions = userList.map(
            (user: FetchUserDetails) => ({
                user_id: user.user_id,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                initials: user.initials,
                full_name:
                    `${user.first_name || ''} ${user.last_name || ''}`.trim(),
            })
        );
    }

    onSelectedUsersChange(users: TypeaheadUser[]): void {
        this.selectedUsersToInvite = users;
    }

    clearTypeaheadSelection(): void {
        if (this.typeaheadInput) {
            this.typeaheadInput.clearSelection();
        }
    }

    onUserSearchChange(query: string): void {
        this.isSearchingUsers = true;
        this.noUsersFound = false;
        this.cdr.detectChanges();
        this.userSearchSubject.next(query);
    }

    private fetchUserSuggestions(query: string): void {
        this.shareService
            .fetchUserDetails(query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    const users: FetchUserDetails[] =
                        response?.data ??
                        (Array.isArray(response) ? response : []);
                    if (users.length > 0) {
                        this.userSuggestions = users.map(
                            (user: FetchUserDetails) => ({
                                user_id: user.user_id,
                                first_name: user.first_name,
                                last_name: user.last_name,
                                email: user.email,
                                initials: user.initials,
                                full_name:
                                    `${user.first_name || ''} ${user.last_name || ''}`.trim(),
                            })
                        );
                        this.noUsersFound = false;
                    } else {
                        this.userSuggestions = [];
                        this.noUsersFound = true;
                    }
                    this.isSearchingUsers = false;
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.userSuggestions = [];
                    this.noUsersFound = true;
                    this.isSearchingUsers = false;
                    this.cdr.detectChanges();
                },
            });
    }

    onSearchInput(event: Event): void {
        this.searchSubject.next(this.searchText);
    }

    selectInviteRole(role: string): void {
        this.showMaxOwnerAlert = false;
        this.inviteRole = role;
    }

    selectFilterRole(role: string): void {
        this.filterRole = role;
        this.filterUsers();
    }

    private readonly rolePriority: Record<string, number> = {
        Owner: 0,
        Editor: 1,
        Commenter: 2,
        Viewer: 3,
    };

    filterUsers(): void {
        let filtered = [...this.users];

        if (this.searchText.trim()) {
            const search = this.searchText.toLowerCase();
            filtered = filtered.filter(
                (user) =>
                    user.name.toLowerCase().includes(search) ||
                    user.email.toLowerCase().includes(search)
            );
        }

        if (this.filterRole !== 'All Roles') {
            filtered = filtered.filter((user) => user.role === this.filterRole);
        }

        filtered.sort((a, b) => {
            // Online users first, then offline.
            const onlineA = this.isUserOnline(a.user_id) ? 0 : 1;
            const onlineB = this.isUserOnline(b.user_id) ? 0 : 1;
            if (onlineA !== onlineB) return onlineA - onlineB;

            const roleDiff =
                (this.rolePriority[a.role] ?? 99) -
                (this.rolePriority[b.role] ?? 99);
            if (roleDiff !== 0) return roleDiff;
            return a.name.localeCompare(b.name);
        });

        this.filteredUsers = filtered;
    }

    inviteUser(): void {
        if (this.selectedUsersToInvite.length === 0 || !this.inviteRole) {
            return;
        }

        if (
            this.inviteRole.toLowerCase() === 'owner' &&
            this.getOwnerCount() + this.selectedUsersToInvite.length >
                this.maxOwners
        ) {
            this.showMaxOwnerAlert = true;
            this.showOwnerMessageAlert = false;
            this.showEditorSelfRoleAlert = false;
            this.cdr.detectChanges();
            return;
        }

        this.isInviting = true;

        const payload: AddUserRoleRequest = {
            project_id: this.projectId,
            project_version_id: this.projectVersionId,
            updated_on: this.updatedOn,
            user_role: this.selectedUsersToInvite.map((user) => ({
                user_id: user.user_id,
                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                role: this.inviteRole.toLowerCase(),
            })),
        };

        this.shareService
            .addProjectUser(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (response?.is_success && response.data) {
                        const projectVersion =
                            response.data.project_version?.[0];
                        const roles =
                            projectVersion?.roles ||
                            response.data.roles ||
                            (Array.isArray(response.data) ? response.data : []);

                        if (projectVersion?.last_refresh_timestamp) {
                            this.updatedOn =
                                projectVersion.last_refresh_timestamp;
                        } else if (response.data.last_refresh_timestamp) {
                            this.updatedOn =
                                response.data.last_refresh_timestamp;
                        }

                        if (roles.length > 0) {
                            this.populateUsersFromRoles(roles);
                            this.updateCollaboratorsInService(this.users);
                        }

                        this.filterUsers();
                        this.updateRolesBasedOnOwnerCount();
                        this.selectedUsersToInvite = [];
                        this.clearTypeaheadSelection();
                        this.inviteRole = 'Editor';
                    }
                    this.isInviting = false;
                    this.cdr.detectChanges();
                },
                error: (error) => {
                    console.error('Error adding users:', error);
                    this.isInviting = false;
                    this.cdr.detectChanges();
                },
            });
    }

    updateUserRole(user: ShareUser, newRole: string): void {
        if (user.role.toLowerCase() === newRole.toLowerCase()) {
            return;
        }

        if (user.role === 'Owner' && this.getOwnerCount() <= 1) {
            this.showOwnerMessageAlert = true;
            this.showEditorSelfRoleAlert = false;
            this.showMaxOwnerAlert = false;
            this.cdr.detectChanges();
            return;
        }

        // Block adding an owner beyond the configured limit.
        if (newRole.toLowerCase() === 'owner' && this.isOwnerLimitReached()) {
            this.showMaxOwnerAlert = true;
            this.showOwnerMessageAlert = false;
            this.showEditorSelfRoleAlert = false;
            this.cdr.detectChanges();
            return;
        }

        // Editors cannot change their own role
        if (
            this.isCurrentUser(user) &&
            this.data?.user_role?.toLowerCase() === 'editor'
        ) {
            this.showEditorSelfRoleAlert = true;
            this.showOwnerMessageAlert = false;
            this.showMaxOwnerAlert = false;
            this.cdr.detectChanges();
            return;
        }

        this.showOwnerMessageAlert = false;
        this.showEditorSelfRoleAlert = false;
        this.showMaxOwnerAlert = false;

        if (this.isCurrentUser(user)) {
            this.confirmOwnRoleChange(user, newRole);
        } else {
            this.performRoleUpdate(user, newRole);
        }
    }

    private confirmOwnRoleChange(user: ShareUser, newRole: string): void {
        const currentRole = user.role;
        const currentUsers = [...this.users];
        const currentUserRole = this.data?.user_role;
        const currentCopyLinkUrl = this.data?.copyLinkUrl;

        this.dialogRef.close();

        const message =
            currentRole === 'Owner'
                ? `You're changing your role from <strong>${currentRole}</strong> to <strong>${this.capitalizeRole(newRole)}</strong>.<br>${currentRole}'s permissions like marking Scenarios as preferred, archiving Project or removing users from Project won't be available.<br>Are you sure you want to continue?`
                : `You're changing your role from <strong>${currentRole}</strong> to <strong>${this.capitalizeRole(newRole)}</strong>.<br>Are you sure you want to continue?`;

        this.confirmDialogService
            .confirmationDialog(
                'Changing Your Role',
                message,
                'confirm',
                's',
                undefined,
                'share-change-role-dialog',
                'Change Your Role'
            )
            .afterClosed()
            .subscribe({
                next: (dialogResponse) => {
                    if (dialogResponse === 'Yes') {
                        this.performRoleUpdate(
                            user,
                            newRole,
                            currentUsers,
                            currentUserRole,
                            currentCopyLinkUrl
                        );
                    } else {
                        this.reopenShareDialog(
                            currentUsers,
                            currentUserRole,
                            currentCopyLinkUrl
                        );
                    }
                },
            });
    }

    private performRoleUpdate(
        user: ShareUser,
        newRole: string,
        cachedUsers?: ShareUser[],
        cachedUserRole?: string,
        cachedCopyLinkUrl?: string
    ): void {
        const request: UpdateProjectUserRoleRequest = {
            project_id: this.projectId,
            project_version_id: this.projectVersionId,
            user_id: user.user_id,
            role: newRole.toLowerCase(),
        };

        this.shareService.updateProjectUserRole(request).subscribe({
            next: (response: any) => {
                if (response?.is_success) {
                    this.handleSuccessfulRoleUpdate(
                        response,
                        user,
                        newRole,
                        cachedUsers,
                        cachedCopyLinkUrl
                    );
                }
            },
            error: (error) => {
                console.error('Error updating user role:', error);
                if (cachedUsers) {
                    this.reopenShareDialog(
                        cachedUsers,
                        cachedUserRole,
                        cachedCopyLinkUrl
                    );
                }
            },
        });
    }

    private handleSuccessfulRoleUpdate(
        response: any,
        user: ShareUser,
        newRole: string,
        cachedUsers?: ShareUser[],
        cachedCopyLinkUrl?: string
    ): void {
        const projectVersion = response.data?.project_version?.[0];
        if (projectVersion?.roles) {
            if (projectVersion.last_refresh_timestamp) {
                this.updatedOn = projectVersion.last_refresh_timestamp;
            }
            this.populateUsersFromRoles(projectVersion.roles);
            this.filterUsers();
        } else if (response.data && Array.isArray(response.data)) {
            this.populateUsersFromRoles(response.data);
            this.filterUsers();
        } else {
            user.role = this.capitalizeRole(newRole);
        }
        if (this.isCurrentUser(user)) {
            this.data.user_role = newRole.toLowerCase();
            const rolePermissionMap =
                this.permissionService.getRolePermissionMap();
            if (rolePermissionMap) {
                this.permissionService.setPermissions(
                    rolePermissionMap,
                    newRole.toLowerCase()
                );
            }
            const projectData = this.designerService.getData();
            if (projectData) {
                projectData.user_role = newRole.toLowerCase();
                this.designerService.setData(projectData);
            }
        }
        this.updateRolesBasedOnOwnerCount();
        if (cachedUsers) {
            this.reopenShareDialog(
                this.users,
                this.data?.user_role,
                cachedCopyLinkUrl
            );
        } else {
            this.cdr.detectChanges();
        }
    }

    isCurrentUser(user: ShareUser): boolean {
        return user.user_id === this.currentUserId;
    }

    getDisabledTooltip(role?: string): string {
        if (!this.isDisabled(role)) return '';
        return this.resourceConstant.noAccessText;
    }

    removeUser(user: ShareUser): void {
        const currentUsers = [...this.users];
        const currentUserRole = this.data?.user_role;
        const currentCopyLinkUrl = this.data?.copyLinkUrl;

        this.dialogRef.close();

        this.confirmDialogService
            .confirmationDialog(
                'Remove User',
                `Are you sure you want to remove ${user.name} from this project?`,
                'confirm',
                's',
                undefined,
                'share-remove-dialog',
                'Confirm'
            )
            .afterClosed()
            .subscribe({
                next: (dialogResponse) => {
                    if (dialogResponse === 'Yes') {
                        const payload = {
                            project_id: this.projectId,
                            project_version_id: this.projectVersionId,
                            user_id: user.user_id,
                            user_role: user.role.toLowerCase(),
                        };

                        this.shareService
                            .deleteProjectUserRole(payload)
                            .subscribe({
                                next: () => {
                                    const index = currentUsers.findIndex(
                                        (u) => u.user_id === user.user_id
                                    );
                                    if (index > -1) {
                                        currentUsers.splice(index, 1);
                                    }
                                    this.updateCollaboratorsInService(
                                        currentUsers
                                    );
                                    this.reopenShareDialog(
                                        currentUsers,
                                        currentUserRole,
                                        currentCopyLinkUrl
                                    );
                                },
                                error: (error) => {
                                    console.error(
                                        'Error removing user:',
                                        error
                                    );
                                    this.reopenShareDialog(
                                        currentUsers,
                                        currentUserRole,
                                        currentCopyLinkUrl
                                    );
                                },
                            });
                    } else {
                        this.reopenShareDialog(
                            currentUsers,
                            currentUserRole,
                            currentCopyLinkUrl
                        );
                    }
                },
            });
    }

    leaveProject(): void {
        const currentUsers = [...this.users];
        const currentUserRole = this.data?.user_role;
        const currentCopyLinkUrl = this.data?.copyLinkUrl;

        const ownerCount = currentUsers.filter(
            (user: any) => user.role?.toLowerCase() === 'owner'
        )?.length;

        if (
            currentUserRole !== 'owner' ||
            (currentUserRole === 'owner' && ownerCount > 1)
        ) {
            this.dialogRef.close();

            this.confirmDialogService
                .confirmationDialog(
                    'Leaving Project',
                    `You're leaving <strong>${this.projectName}</strong>. You will no longer have access to it. <br> Are you sure you want to continue?`,
                    'confirm',
                    's',
                    undefined,
                    'share-leave-dialog',
                    'Leave Project'
                )
                .afterClosed()
                .subscribe({
                    next: (dialogResponse) => {
                        if (dialogResponse === 'Yes') {
                            const currentUser = this.users.find(
                                (u) => u.user_id === this.currentUserId
                            );
                            if (currentUser) {
                                const payload = {
                                    project_id: this.projectId,
                                    project_version_id: this.projectVersionId,
                                    user_id: this.currentUserId,
                                    user_role: currentUser.role.toLowerCase(),
                                };

                                this.shareService
                                    .deleteProjectUserRole(payload)
                                    .subscribe({
                                        next: () => {
                                            this.router.navigate([
                                                '/app/project',
                                            ]);
                                        },
                                        error: (error) => {
                                            console.error(
                                                'Error leaving project:',
                                                error
                                            );
                                            this.reopenShareDialog(
                                                currentUsers,
                                                currentUserRole,
                                                currentCopyLinkUrl
                                            );
                                        },
                                    });
                            }
                        } else {
                            this.reopenShareDialog(
                                currentUsers,
                                currentUserRole,
                                currentCopyLinkUrl
                            );
                        }
                    },
                });
        } else {
            this.showOwnerMessageAlert = false;
            this.cdr.detectChanges();
            this.showOwnerMessageAlert = true;
        }
    }

    private getUsersAsCollaborators(users: ShareUser[]): Collaborator[] {
        return users.map((user) => {
            const nameParts = user.name.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            return {
                user_id: user.user_id,
                first_name: firstName,
                last_name: lastName,
                full_name: user.name,
                initials: this.getInitials(user.name),
                email: user.email,
                role: user.role.toLowerCase(),
                current_user: user.user_id === this.currentUserId,
            };
        });
    }

    private updateCollaboratorsInService(users: ShareUser[]): void {
        const collaborators = this.getUsersAsCollaborators(users);
        const projectData = this.designerService.getData() as any;
        if (projectData) {
            projectData.collaborators = collaborators;
            this.designerService.setData(projectData);
            this.designerService.emitProjectMetaData(projectData);
        }
    }

    private reopenShareDialog(
        users: ShareUser[],
        userRole?: string,
        copyLinkUrl?: string
    ): void {
        this.rdsDialogService.open(ShareComponent, {
            size: 'l',
            data: {
                project_id: this.projectId,
                project_version_id: this.projectVersionId,
                project_name: this.projectName,
                updated_on: this.updatedOn,
                copyLinkUrl: copyLinkUrl ?? this.data?.copyLinkUrl,
                user_role: userRole ?? this.data?.user_role,
                max_owners: this.maxOwners,
                activeUserIds: [...this.activeUserIds],
            },
        });
    }

    getInitials(name: string): string {
        if (!name) return '';

        // Filter out empty strings caused by consecutive spaces
        const parts = name.trim().split(' ').filter(Boolean);
        if (parts.length === 0) return '';

        if (parts.length >= 2) {
            const firstLetter = parts[0]?.[0] ?? '';
            const lastLetter = parts.at(-1)?.[0] ?? '';
            return `${firstLetter}${lastLetter}`.toUpperCase();
        }

        return parts[0].substring(0, 2).toUpperCase();
    }

    close(): void {
        const updatedCollaborators = this.getUsersAsCollaborators(this.users);
        this.dialogRef.close({
            collaborators: updatedCollaborators,
            user_role: this.data?.user_role,
            max_owners: this.maxOwners,
        });
    }

    openRolesDescription(): void {
        const updatedCollaborators = this.getUsersAsCollaborators(this.users);
        const currentData: ShareDialogData = {
            ...this.data,
            collaborators: updatedCollaborators,
            max_owners: this.maxOwners,
            activeUserIds: [...this.activeUserIds],
            // Carry over typed-but-not-yet-invited users and the chosen role so
            // they survive the close/reopen when viewing the roles description.
            pendingInviteRole: this.inviteRole,
            pendingInvites: [...this.selectedUsersToInvite],
        };

        this.dialogRef.close({ openRolesDescription: true });

        const rolesRef = this.rdsDialogService.open(RolesDescriptionComponent, {
            size: 'm',
        });

        rolesRef.afterClosed().subscribe(() => {
            const shareRef = this.rdsDialogService.open(ShareComponent, {
                size: 'l',
                disableClose: true,
                data: currentData,
            });

            shareRef.afterClosed().subscribe((result: any) => {
                if (result?.openRolesDescription) {
                    return;
                }
                if (result?.collaborators && currentData.collaborators) {
                    currentData.collaborators = result.collaborators;
                }
                if (result?.user_role) {
                    currentData.user_role = result.user_role;
                }
            });
        });
    }
}
